import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from 'react-redux';
import { getRoot } from '../../../../abstract/redux/slice/structureSlice';
import { exportToYaml } from '../../../../abstract/controller/modelYamlExport';
import { updateModelStatus, MODEL_STATUS } from './utils/AiUtils';
import "./AiYamlDiffViewer.scss";

const DIFF_TYPES = {
  SAME: 'same',
  ADDED: 'added',
  REMOVED: 'removed',
  CHANGED: 'changed'
};

const LINE_CLASSES = {
  SAME: 'line-same',
  ADDED: 'line-added',
  REMOVED: 'line-removed',
  CHANGED_OLD: 'line-changed-old',
  CHANGED_NEW: 'line-changed-new',
  DECIDED: 'line-decided',
  ACTIVE: 'active-line',
  HOVERABLE: 'hoverable-line'
};

const MESSAGES = {
  NO_DIFF: 'No diff data available. Waiting for data...',
  WAITING: 'Waiting for diff data...',
  STATUS_UPDATED: 'Model status updated to',
  GENERATING_DIFF: 'Generating diff from nodeRoot and newYamlData',
  USING_DIFF: 'Using provided diffLines',
  EXPORT_FAILED: 'Failed to export nodeRoot to YAML',
  PARSE_FAILED: 'Failed to parse final YAML',
  SESSION_STORAGE_FAILED: 'sessionStorage failed:',
  FAILED_TO_APPROVE: 'Failed to approve:',
  FAILED_TO_REJECT: 'Failed to reject:',
  UPDATED_MODEL_READY: 'Updated model ready for next chat message'
};

const UI_TEXT = {
  TITLE_UNIFIED: 'Unified Diff View',
  DESC_COMPARING: 'Comparing current model with AI suggestions',
  BTN_APPROVE: 'Approve All',
  BTN_REJECT: 'Reject All',
  TOOLTIP_ACCEPT_REMOVAL: 'Accept removal',
  TOOLTIP_KEEP_OLD: 'Keep old data',
  TOOLTIP_ADD_NEW: 'Add new data',
  TOOLTIP_DONT_ADD: "Don't add",
  TOOLTIP_USE_NEW: 'Use new data'
};

const CONFIG = {
  INDENT_THRESHOLD: 10,
  PARAM_KEYWORD: 'parameter:',
  LOOKAHEAD_RANGE: 10,
  DECISION_DELAY: 10
};

const generateDiff = (oldYaml, newYaml) => {
  if (!oldYaml || !newYaml) return [];
  
  const oldLines = oldYaml.split('\n');
  const newLines = newYaml.split('\n');
  const diff = [];
  
  let oldIdx = 0;
  let newIdx = 0;
  
  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    const oldLine = oldLines[oldIdx];
    const newLine = newLines[newIdx];
    
    if (oldIdx >= oldLines.length) {
      diff.push({ type: DIFF_TYPES.ADDED, content: newLine });
      newIdx++;
    } else if (newIdx >= newLines.length) {
      diff.push({ type: DIFF_TYPES.REMOVED, content: oldLine });
      oldIdx++;
    } else if (oldLine === newLine) {
      diff.push({ type: DIFF_TYPES.SAME, content: oldLine });
      oldIdx++;
      newIdx++;
    } else {
      const oldTrimmed = oldLine.trim();
      const newTrimmed = newLine.trim();
      const oldKey = oldTrimmed.split(':')[0];
      const newKey = newTrimmed.split(':')[0];
      
      if (oldKey && newKey && oldKey === newKey) {
        diff.push({ 
          type: DIFF_TYPES.CHANGED, 
          oldContent: oldLine, 
          newContent: newLine 
        });
        oldIdx++;
        newIdx++;
      } else {
        const oldLineInNew = newLines.slice(newIdx + 1, newIdx + CONFIG.LOOKAHEAD_RANGE).indexOf(oldLine);
        const newLineInOld = oldLines.slice(oldIdx + 1, oldIdx + CONFIG.LOOKAHEAD_RANGE).indexOf(newLine);
        
        if (oldLineInNew !== -1 && (newLineInOld === -1 || oldLineInNew < newLineInOld)) {
          diff.push({ type: DIFF_TYPES.ADDED, content: newLine });
          newIdx++;
        } else if (newLineInOld !== -1) {
          diff.push({ type: DIFF_TYPES.REMOVED, content: oldLine });
          oldIdx++;
        } else {
          diff.push({ 
            type: DIFF_TYPES.CHANGED, 
            oldContent: oldLine, 
            newContent: newLine 
          });
          oldIdx++;
          newIdx++;
        }
      }
    }
  }
  
  return diff;
};

const normalizeDiffLines = (diffLines) => {
  if (!diffLines || !Array.isArray(diffLines)) {
    return [];
  }

  const validTypes = Object.values(DIFF_TYPES);
  
  return diffLines.map((line) => {
    if (!line || typeof line !== 'object') {
      return { type: DIFF_TYPES.SAME, content: '' };
    }

    if (!validTypes.includes(line.type)) {
      return { ...line, type: DIFF_TYPES.SAME };
    }

    return line;
  });
};

export function YamlDiffViewer({ diffLines, newYamlData, onApprove, onReject, onModelUpdate }) {
  const componentRef = useRef(null);
  const _nodeRoot = useSelector(getRoot);
  const [approvedGroups, setApprovedGroups] = useState(new Set());
  const [rejectedGroups, setRejectedGroups] = useState(new Set());
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [normalizedDiffLines, setNormalizedDiffLines] = useState([]);
  
  const approvedGroupsRef = useRef(approvedGroups);
  const rejectedGroupsRef = useRef(rejectedGroups);
  
  useEffect(() => {
    approvedGroupsRef.current = approvedGroups;
  }, [approvedGroups]);
  
  useEffect(() => {
    rejectedGroupsRef.current = rejectedGroups;
  }, [rejectedGroups]);

  const currentNodeRootYaml = useMemo(() => {
    if (!_nodeRoot) return '';

    try {
      return exportToYaml(_nodeRoot);
    } catch (e) {
      console.log(`${MESSAGES.EXPORT_FAILED}: ${e.message}`);
      return '';
    }
  }, [_nodeRoot]);

  useEffect(() => {
    let diffToNormalize = null;
    
    if (newYamlData && currentNodeRootYaml) {
      diffToNormalize = generateDiff(currentNodeRootYaml, newYamlData);
    } else if (diffLines) {
      diffToNormalize = diffLines;
    }
    
    if (diffToNormalize) {
      const normalized = normalizeDiffLines(diffToNormalize);
      setNormalizedDiffLines(normalized);
      setApprovedGroups(new Set());
      setRejectedGroups(new Set());
    }
  }, [newYamlData, currentNodeRootYaml, diffLines]);

  const trimContent = (content) => {
    return content?.trim() || '';
  };

  const groupLinesByParameter = useCallback((lines) => {
    const groups = [];
    let currentParamGroup = null;
    let currentChangeGroup = null;

    lines.forEach((line, idx) => {
      const content = line.content || line.oldContent || line.newContent || '';
      const trimmed = content.trim();
      const indent = content.match(/^\s*/)?.[0].length || 0;

      const isParamDef = indent < CONFIG.INDENT_THRESHOLD && trimmed.includes(CONFIG.PARAM_KEYWORD);

      if (isParamDef) {
        if (currentChangeGroup && currentChangeGroup.lines.length > 0) {
          groups.push(currentChangeGroup);
        }
        currentParamGroup = { paramName: trimmed, startIdx: idx };
        currentChangeGroup = null;
      }

      const isChanged = line.type === DIFF_TYPES.REMOVED || 
                       line.type === DIFF_TYPES.ADDED || 
                       (line.type === DIFF_TYPES.CHANGED && trimContent(line.oldContent) !== trimContent(line.newContent));

      if (isChanged) {
        if (!currentChangeGroup) {
          currentChangeGroup = {
            startIdx: idx,
            endIdx: idx,
            lines: [line],
            paramGroup: currentParamGroup
          };
        } else if (currentChangeGroup.paramGroup === currentParamGroup) {
          currentChangeGroup.endIdx = idx;
          currentChangeGroup.lines.push(line);
        } else {
          if (currentChangeGroup.lines.length > 0) {
            groups.push(currentChangeGroup);
          }
          currentChangeGroup = {
            startIdx: idx,
            endIdx: idx,
            lines: [line],
            paramGroup: currentParamGroup
          };
        }
      } else {
        if (currentChangeGroup && currentChangeGroup.lines.length > 0) {
          groups.push(currentChangeGroup);
          currentChangeGroup = null;
        }
      }
    });

    if (currentChangeGroup && currentChangeGroup.lines.length > 0) {
      groups.push(currentChangeGroup);
    }

    return groups;
  }, []);

  const applyDecisionsToNodeRoot = useCallback((approvedSetParam, rejectedSetParam) => {
    if (!normalizedDiffLines || normalizedDiffLines.length === 0) {
      return currentNodeRootYaml;
    }

    const finalLines = [];
    const changeGroups = groupLinesByParameter(normalizedDiffLines);
    const approved = approvedSetParam ?? approvedGroupsRef.current;
    // const rejected = rejectedSetParam ?? rejectedGroupsRef.current;

    normalizedDiffLines.forEach((line, idx) => {
      const group = changeGroups.find(g => idx >= g.startIdx && idx <= g.endIdx);
      const groupKey = group ? `${group.startIdx}-${group.endIdx}` : null;
      const isApproved = groupKey && approved.has(groupKey);
      // const isRejected = groupKey && rejected.has(groupKey);

      if (line.type === DIFF_TYPES.SAME) {
        finalLines.push(line.content);
      } else if (line.type === DIFF_TYPES.REMOVED) {
        if (!isApproved) {
          finalLines.push(line.content);
        }
      } else if (line.type === DIFF_TYPES.ADDED) {
        if (isApproved) {
          finalLines.push(line.content);
        }
      } else if (line.type === DIFF_TYPES.CHANGED) {
        const trimmedOld = trimContent(line.oldContent);
        const trimmedNew = trimContent(line.newContent);

        if (trimmedOld === trimmedNew) {
          finalLines.push(line.oldContent);
        } else {
          if (isApproved) {
            finalLines.push(line.newContent);
          } else {
            finalLines.push(line.oldContent);
          }
        }
      }
    });

    const finalYaml = finalLines.join('\n');

    try {
      let parsedModel = null;
      if (typeof window !== "undefined" && window.yaml && typeof window.yaml.load === 'function') {
        parsedModel = window.yaml.load(finalYaml);
        window._nodeRootUpdated = parsedModel;
      } else {
        try {
          parsedModel = JSON.parse(finalYaml);
          window._nodeRootUpdated = parsedModel;
        } catch (e) {
          // Cannot parse; leave parsedModel as null but still set YAML string
        }
      }

      window._nodeRootYaml = finalYaml;

      
      if (onModelUpdate) {
        onModelUpdate(finalYaml, parsedModel);
      }
    } catch (e) {
    }

    return finalYaml;
  }, [normalizedDiffLines, groupLinesByParameter, currentNodeRootYaml, onModelUpdate]);

  const handleLineHover = (groupKey) => {
    setActiveGroupKey(groupKey);
  };

  const handleLineLeave = () => {
    setActiveGroupKey(null);
  };

  const handleGroupApprove = useCallback((startIdx, endIdx) => {
    const groupKey = `${startIdx}-${endIdx}`;

    const newApprovedSet = new Set(approvedGroupsRef.current);
    newApprovedSet.add(groupKey);
    const newRejectedSet = new Set(rejectedGroupsRef.current);
    newRejectedSet.delete(groupKey);

    setApprovedGroups(newApprovedSet);
    approvedGroupsRef.current = newApprovedSet;
    setRejectedGroups(newRejectedSet);
    rejectedGroupsRef.current = newRejectedSet;

    setActiveGroupKey(null);

    try {
      const finalYaml = applyDecisionsToNodeRoot(newApprovedSet, newRejectedSet);
      sessionStorage.setItem('finalYaml', finalYaml ?? '');
    } catch (err) {
      
    }
  }, [applyDecisionsToNodeRoot]);

  const handleGroupReject = useCallback((startIdx, endIdx) => {
    const groupKey = `${startIdx}-${endIdx}`;
    
    const newRejectedSet = new Set(rejectedGroupsRef.current);
    newRejectedSet.add(groupKey);
    const newApprovedSet = new Set(approvedGroupsRef.current);
    newApprovedSet.delete(groupKey);

    setRejectedGroups(newRejectedSet);
    rejectedGroupsRef.current = newRejectedSet;
    setApprovedGroups(newApprovedSet);
    approvedGroupsRef.current = newApprovedSet;

    setActiveGroupKey(null);

    try {
      const finalYaml = applyDecisionsToNodeRoot(newApprovedSet, newRejectedSet);
      sessionStorage.setItem('finalYaml', finalYaml ?? '');
    } catch (err) {
    }
  }, [applyDecisionsToNodeRoot]);

  const renderLineNumber = (oldNum, newNum, width) => {
    const formatNum = (num) => {
      if (num === null || num === undefined) return '\u00A0'.repeat(width);
      const numStr = String(num);
      const padding = '\u00A0'.repeat(Math.max(0, width - numStr.length));
      return padding + numStr;
    };
    
    return (
      <span className="line-number-dual">
        <span className="line-num-old">{formatNum(oldNum)}</span>
        <span className="line-num-new">{formatNum(newNum)}</span>
      </span>
    );
  };

  const highlightDifferences = (oldText, newText) => {
    const oldIndent = oldText?.match(/^\s*/)?.[0] ?? "";
    const newIndent = newText?.match(/^\s*/)?.[0] ?? "";
    
    const oldCore = oldText?.slice(oldIndent.length) || '';
    const newCore = newText?.slice(newIndent.length) || '';
    const oldWords = oldCore.split(/(\s+|:)/);
    const newWords = newCore.split(/(\s+|:)/);
    const maxLength = Math.max(oldWords.length, newWords.length);
    const oldResult = [];
    const newResult = [];
    
    for (let i = 0; i < maxLength; i++) {
      const oldWord = oldWords[i] || "";
      const newWord = newWords[i] || "";
      if (oldWord === newWord) {
        oldResult.push(<span key={`old-${i}`}>{oldWord}</span>);
        newResult.push(<span key={`new-${i}`}>{newWord}</span>);
      } else {
        oldResult.push(oldWord && <span key={`old-${i}`} className="old-word">{oldWord}</span>);
        newResult.push(newWord && <span key={`new-${i}`} className="new-word">{newWord}</span>);
      }
    }
    return { old: <>{oldIndent}{oldResult}</>, new: <>{newIndent}{newResult}</> };
  };

  const renderKeyValueColored = (content) => {
    if (typeof content === "object" && content !== null && (content.$typeof || content.type)) return content;
    if (Array.isArray(content)) return content;
    if (typeof content === "string") {
      if (/^\s*[^:]+:\s*$/.test(content)) {
        const match = content.match(/^(\s*)([^:]+)(:)\s*$/);
        return <><span>{match?.[1] || ""}</span><span>{match?.[2] || content}</span><span>{match?.[3] || ""}</span></>;
      }
      const match = content.match(/^(\s*)([^:]+)(:)(.*)$/);
      if (match) {
        const [, leadingSpace, key, colon, value] = match;
        return <><span>{leadingSpace}</span><span>{key}</span><span>{colon}</span><span>{value}</span></>;
      }
    }
    return <span>{content}</span>;
  };

  const handleApprove = async () => {
    try {
      const changeGroups = groupLinesByParameter(normalizedDiffLines);
      const newApproved = new Set(approvedGroupsRef.current);
      const newRejected = new Set(rejectedGroupsRef.current);
      changeGroups.forEach(g => {
        const key = `${g.startIdx}-${g.endIdx}`;
        newApproved.add(key);
        newRejected.delete(key);
      });

      setApprovedGroups(newApproved);
      approvedGroupsRef.current = newApproved;
      setRejectedGroups(newRejected);
      rejectedGroupsRef.current = newRejected;

      const finalYaml = applyDecisionsToNodeRoot(newApproved, newRejected);
      sessionStorage.setItem('finalYaml', finalYaml ?? '');

      updateModelStatus(MODEL_STATUS.APPROVED);
     
      onApprove?.();
    } catch (error) {
    }
  };

  const handleReject = async () => {
    try {
      setApprovedGroups(new Set());
      approvedGroupsRef.current = new Set();
      setRejectedGroups(new Set());
      rejectedGroupsRef.current = new Set();

      const finalYaml = currentNodeRootYaml;
      sessionStorage.setItem('finalYaml', finalYaml ?? '');

      updateModelStatus(MODEL_STATUS.REJECTED);

      onReject?.();
    } catch (error) {
    }
  };

  if (!normalizedDiffLines || normalizedDiffLines.length === 0) {
    return (
      <div className="viewer" ref={componentRef}>
        <div className="header">
          <div className="header-pane">
            <div className="header-title-unified">{UI_TEXT.TITLE_UNIFIED}</div>
            <div>
              {MESSAGES.NO_DIFF}
            </div>
          </div>
        </div>
        <div className="content">
          <div className="content-pane unified">
            <pre>
              {MESSAGES.WAITING}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  const changeGroups = groupLinesByParameter(normalizedDiffLines);
  
  let oldLineNum = 1;
  let newLineNum = 1;
  const lineNumbers = [];
  
  normalizedDiffLines.forEach((line) => {
    if (line.type === DIFF_TYPES.SAME) {
      lineNumbers.push({ oldNum: oldLineNum++, newNum: newLineNum++ });
    } else if (line.type === DIFF_TYPES.REMOVED) {
      lineNumbers.push({ oldNum: oldLineNum++, newNum: null });
    } else if (line.type === DIFF_TYPES.ADDED) {
      lineNumbers.push({ oldNum: null, newNum: newLineNum++ });
    } else if (line.type === DIFF_TYPES.CHANGED) {
      lineNumbers.push({ oldNum: oldLineNum++, newNum: null });
      lineNumbers.push({ oldNum: null, newNum: newLineNum++ });
    }
  });
  
  const maxOldNum = Math.max(...lineNumbers.filter(n => n.oldNum !== null).map(n => n.oldNum), 1);
  const maxNewNum = Math.max(...lineNumbers.filter(n => n.newNum !== null).map(n => n.newNum), 1);
  const maxWidth = Math.max(String(maxOldNum).length, String(maxNewNum).length);

  return (
    <div className="viewer" ref={componentRef}>
      <div className="header">
        <div className="header-pane">
          <div className="header-title-unified">{UI_TEXT.TITLE_UNIFIED}</div>
          <div>{UI_TEXT.DESC_COMPARING}</div>
        </div>
      </div>
      <div className="content">
        <div className="content-pane unified">
          <pre>
            {normalizedDiffLines.map((line, idx) => {
              const group = changeGroups.find(g => idx >= g.startIdx && idx <= g.endIdx);
              const isLastInGroup = group && idx === group.endIdx;
              const groupKey = group ? `${group.startIdx}-${group.endIdx}` : null;
              const isApproved = groupKey && approvedGroups.has(groupKey);
              const isRejected = groupKey && rejectedGroups.has(groupKey);
              const isActive = groupKey && activeGroupKey === groupKey;
              const isDecided = isApproved || isRejected;
              const isInGroup = group !== null;
              
              let lineNumIdx = 0;
              for (let i = 0; i < idx; i++) {
                lineNumIdx++;
                if (normalizedDiffLines[i].type === DIFF_TYPES.CHANGED) {
                  lineNumIdx++;
                }
              }
              
              if (line.type === DIFF_TYPES.SAME) {
                const nums = lineNumbers[lineNumIdx];
                return (
                  <div key={`line-${idx}`} className={LINE_CLASSES.SAME}>
                    {renderLineNumber(nums.oldNum, nums.newNum, maxWidth)}
                    {renderKeyValueColored(line.content)}
                  </div>
                );
              }
              
              if (line.type === DIFF_TYPES.REMOVED) {
                if (isApproved) return null;
                const nums = lineNumbers[lineNumIdx];
                const className = isRejected ? LINE_CLASSES.DECIDED : LINE_CLASSES.REMOVED;
                return (
                  <div 
                    key={`line-${idx}`} 
                    className={`${className} ${isActive ? LINE_CLASSES.ACTIVE : ''} ${!isDecided && isInGroup ? LINE_CLASSES.HOVERABLE : ''}`}
                    onMouseEnter={() => !isDecided && isInGroup && handleLineHover(groupKey)}
                    onMouseLeave={() => !isDecided && isInGroup && handleLineLeave()}
                  >
                    {renderLineNumber(nums.oldNum, null, maxWidth)}
                    {renderKeyValueColored(line.content)}
                    {isActive && isLastInGroup && (
                      <div className="line-actions-popup">
                        <button onClick={(e) => { e.stopPropagation(); handleGroupApprove(group.startIdx, group.endIdx); }} className="approve-btn-popup" title={UI_TEXT.TOOLTIP_ACCEPT_REMOVAL}>✓</button>
                        <button onClick={(e) => { e.stopPropagation(); handleGroupReject(group.startIdx, group.endIdx); }} className="reject-btn-popup" title={UI_TEXT.TOOLTIP_KEEP_OLD}>✗</button>
                      </div>
                    )}
                  </div>
                );
              }
              
              if (line.type === DIFF_TYPES.ADDED) {
                if (isRejected) return null;
                const nums = lineNumbers[lineNumIdx];
                const className = isApproved ? LINE_CLASSES.DECIDED : LINE_CLASSES.ADDED;
                return (
                  <div 
                    key={`line-${idx}`} 
                    className={`${className} ${isActive ? LINE_CLASSES.ACTIVE : ''} ${!isDecided && isInGroup ? LINE_CLASSES.HOVERABLE : ''}`}
                    onMouseEnter={() => !isDecided && isInGroup && handleLineHover(groupKey)}
                    onMouseLeave={() => !isDecided && isInGroup && handleLineLeave()}
                  >
                    {renderLineNumber(null, nums.newNum, maxWidth)}
                    {renderKeyValueColored(line.content)}
                    {isActive && isLastInGroup && (
                      <div className="line-actions-popup">
                        <button onClick={(e) => { e.stopPropagation(); handleGroupApprove(group.startIdx, group.endIdx); }} className="approve-btn-popup" title={UI_TEXT.TOOLTIP_ADD_NEW}>✓</button>
                        <button onClick={(e) => { e.stopPropagation(); handleGroupReject(group.startIdx, group.endIdx); }} className="reject-btn-popup" title={UI_TEXT.TOOLTIP_DONT_ADD}>✗</button>
                      </div>
                    )}
                  </div>
                );
              }
              
              if (line.type === DIFF_TYPES.CHANGED) {
                const trimmedOld = trimContent(line.oldContent);
                const trimmedNew = trimContent(line.newContent);
                
                if (trimmedOld === trimmedNew) {
                  const nums = lineNumbers[lineNumIdx];
                  return (
                    <div key={`line-${idx}`} className={LINE_CLASSES.SAME}>
                      {renderLineNumber(nums.oldNum, nums.newNum, maxWidth)}
                      {renderKeyValueColored(line.oldContent)}
                    </div>
                  );
                }
                
                const highlighted = highlightDifferences(line.oldContent, line.newContent);
                const oldNums = lineNumbers[lineNumIdx];
                const newNums = lineNumbers[lineNumIdx + 1];
                
                if (isApproved) {
                  return (
                    <div key={`line-${idx}`} className={LINE_CLASSES.DECIDED}>
                      {renderLineNumber(null, newNums.newNum, maxWidth)}
                      {renderKeyValueColored(line.newContent)}
                    </div>
                  );
                }
                
                if (isRejected) {
                  return (
                    <div key={`line-${idx}`} className={LINE_CLASSES.DECIDED}>
                      {renderLineNumber(oldNums.oldNum, null, maxWidth)}
                      {renderKeyValueColored(line.oldContent)}
                    </div>
                  );
                }
                
                return (
                  <React.Fragment key={`line-${idx}`}>
                    <div 
                      className={`${LINE_CLASSES.CHANGED_OLD} ${isActive ? LINE_CLASSES.ACTIVE : ''} ${!isDecided && isInGroup ? LINE_CLASSES.HOVERABLE : ''}`}
                      onMouseEnter={() => !isDecided && isInGroup && handleLineHover(groupKey)}
                      onMouseLeave={() => !isDecided && isInGroup && handleLineLeave()}
                    >
                      {renderLineNumber(oldNums.oldNum, null, maxWidth)}
                      {renderKeyValueColored(highlighted.old)}
                    </div>
                    <div 
                      className={`${LINE_CLASSES.CHANGED_NEW} ${isActive ? LINE_CLASSES.ACTIVE : ''} ${!isDecided && isInGroup ? LINE_CLASSES.HOVERABLE : ''}`}
                      onMouseEnter={() => !isDecided && isInGroup && handleLineHover(groupKey)}
                      onMouseLeave={() => !isDecided && isInGroup && handleLineLeave()}
                    >
                      {renderLineNumber(null, newNums.newNum, maxWidth)}
                      {renderKeyValueColored(highlighted.new)}
                      {isActive && isLastInGroup && (
                        <div className="line-actions-popup">
                          <button onClick={(e) => { e.stopPropagation(); handleGroupApprove(group.startIdx, group.endIdx); }} className="approve-btn-popup" title={UI_TEXT.TOOLTIP_USE_NEW}>✓</button>
                          <button onClick={(e) => { e.stopPropagation(); handleGroupReject(group.startIdx, group.endIdx); }} className="reject-btn-popup" title={UI_TEXT.TOOLTIP_KEEP_OLD}>✗</button>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              }
              
              return null;
            })}
          </pre>
        </div>
      </div>
      <div className="footer">
        <button onClick={handleApprove} className="approve-btn">{UI_TEXT.BTN_APPROVE}</button>
        <button onClick={handleReject} className="reject-btn">{UI_TEXT.BTN_REJECT}</button>
      </div>
    </div>
  );
}

export default YamlDiffViewer;